import { addDays, format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { logger } from '@modules/logger';
import {
  BUSINESS_ONBOARDING_ROUTES,
  FALLBACK_MIN_UNIT_QUANTITY,
} from 'app-next/onboarding/business/constants';

import { WorkspaceType } from '.cache/__types__';
import { useCheckout, usePrices } from '@hooks/billing-subscription';
import { useGetWorkspacesQuery } from '@hooks/workspace/query.graphql';
import { notificationContext } from '@context/notifications';

import Badge from '@components/Badge/Badge';

import { Interval, IntervalPricing } from './Interval';
import { Box, Button, Heading, Text } from '@ui-lib';

import styles from '../UpgradeDialog.module.scss';

import { analytics } from '@modules/analytics';
import { BillingSubscriptionPriceDetailsFragment } from '@graphql/fragments/billing-subscription/price.graphql';
import {
  getEffectiveMonthlyDisplayPriceFromYearlyPrice,
  getEffectiveYearlyDiscountPercentage,
  useFormatCurrency,
} from '@modules/pricing-helpers';
import { useContext, useState, useEffect } from 'react';

interface PriceIntervalSelectionProps {
  type: WorkspaceType;
  includeBackBtn: boolean;
  onBack: () => void;
}

const PaymentSection: React.FC<React.PropsWithChildren<PriceIntervalSelectionProps>> = ({
  type,
  includeBackBtn,
  onBack,
}) => {
  const { data } = useGetWorkspacesQuery({
    fetchPolicy: 'network-only',
  });

  const { prices, products, currency, loading: loadingPrices } = usePrices();
  const { loading, startCheckout } = useCheckout();
  const { notify } = useContext(notificationContext);
  const formatCurrency = useFormatCurrency({ currencySymbol: currency });
  const businessOnboardingV2Enabled = true;

  const product = products.find((product) => product.type === type);
  const minCardCount = product?.min_card_count ?? FALLBACK_MIN_UNIT_QUANTITY;

  const yearlyPrice = prices.find((price) => price.interval === 'year' && price.product?.type === type);
  const monthlyPrice = prices.find((price) => price.interval === 'month' && price.product?.type === type);

  const [price, setPrice] = useState(yearlyPrice);

  useEffect(() => {
    if (!loadingPrices) {
      setPrice(yearlyPrice);
    }
  }, [loadingPrices, setPrice, yearlyPrice]);

  const dueDate = addDays(new Date(), product?.trial_period_days ?? 7);
  const formattedDueDate = format(dueDate, 'd MMMM yyyy');

  function handleBillingIntervalChange(price: BillingSubscriptionPriceDetailsFragment) {
    setPrice(price);
    analytics.track({
      name: 'billing_changedbillinginterval',
      properties: {
        page: 'upgrade-dialog',
        subscription_plan: type === 'INDIVIDUAL' ? 'premium' : 'business',
        subscription_term: price.interval === 'year' ? 'annual' : 'monthly',
      },
    });
  }

  async function startCheckoutSession() {
    if (!price) {
      throw new Error('Cannot start checkout without a price.');
    }

    const workspace = data?.workspaces?.find((workspace) => workspace?.type === type);

    switch (type) {
      case 'INDIVIDUAL': {
        if (!workspace) {
          throw new Error('User does not have an "INDIVIDUAL" workspace.');
        }

        return await startCheckout({
          priceId: price.id,
          workspaceId: workspace.id,
        });
      }
      case 'TEAM': {
        return await startCheckout({
          priceId: price.id,
          workspaceId: workspace?.id,
          createWorkspace: !workspace,
          seats: minCardCount,
          successUrl: businessOnboardingV2Enabled
            ? `${origin}${BUSINESS_ONBOARDING_ROUTES.SETUP_WORKSPACE}`
            : undefined,
        });
      }
    }
  }

  async function onClick() {
    try {
      await startCheckoutSession();
    } catch (error) {
      logger.error(`Unable to start checkout session for user.`, { error });

      notify({
        timeout: 5000,
        title: 'Sorry, something went wrong. Try again shortly.',
        type: 'error',
        autoHideAfterTimeout: true,
      });
    }
  }

  if (!product || !monthlyPrice || !yearlyPrice || loadingPrices) {
    return null;
  }

  return (
    <div className={styles['price-interval-selection-root']}>
      {includeBackBtn ? (
        <div className={styles['back-btn-container']}>
          <Button variant="text" onClick={onBack} iconLeading={<FontAwesomeIcon icon={faArrowLeft} />} />
        </div>
      ) : null}
      <div className={styles['price-interval-selection-section']}>
        <Box marginBottom={'medium'} gap={'medium'} flexDirection={'column'} display={'flex'}>
          <Heading level="4">Try Blinq {type === 'INDIVIDUAL' ? 'Premium' : 'Business'} for free</Heading>
          <ul className={styles['assurances']}>
            <li className={styles['assurance']}>
              <div className={styles['tick-container']}>
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <Text>Free {product.trial_period_days} day trial, cancel any time</Text>
            </li>
            <li className={styles['assurance']}>
              <div className={styles['tick-container']}>
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <Text>We&apos;ll remind you before your trial ends</Text>
            </li>
          </ul>
        </Box>

        <div className={styles['interval-selection-section']}>
          <div className={styles['interval-selection']}>
            <Interval
              name="Annually"
              selected={price?.interval === 'year'}
              onSelect={() => handleBillingIntervalChange(yearlyPrice)}
              costSaving={
                <Badge type="attention" size="small">
                  <span className={styles['discount-badge']}>
                    Best Value - Save{' '}
                    {getEffectiveYearlyDiscountPercentage({
                      yearlyPriceUnitAmount: yearlyPrice.unit_amount,
                      monthlyPriceUnitAmount: monthlyPrice.unit_amount,
                      decimalPlaces: type === 'TEAM' ? 2 : 0,
                    })}
                  </span>
                </Badge>
              }
              pricing={
                <IntervalPricing
                  primaryPrice={`${formatCurrency(yearlyPrice.unit_amount)} ${
                    type === 'TEAM' ? ' per card *' : ''
                  }`}
                  secondaryPrice={`${getEffectiveMonthlyDisplayPriceFromYearlyPrice(
                    yearlyPrice.unit_amount,
                    formatCurrency
                  )}/month`}
                />
              }
            />
          </div>
          <div className={styles['interval-selection']}>
            <Interval
              name="Monthly"
              selected={price?.interval === 'month'}
              onSelect={() => {
                handleBillingIntervalChange(monthlyPrice);
              }}
              pricing={
                <IntervalPricing
                  primaryPrice={`${formatCurrency(monthlyPrice.unit_amount)} ${
                    type === 'TEAM' ? ' per card *' : ''
                  }`}
                />
              }
            />
          </div>
        </div>
        <div className={styles['payment-invoices']}>
          <span className={styles['payment-condition']}>
            {type === 'INDIVIDUAL' ? undefined : `* ${minCardCount} cards minimum`}
          </span>
          <div className={styles['payment-invoice']} data-primary={false}>
            <div className={styles['payment-due-date']}>
              <span>Due {formattedDueDate}</span>
            </div>
            <div className={styles['payment-amount']}>
              {price?.interval === 'month'
                ? formatCurrency(monthlyPrice.unit_amount * (type === 'TEAM' ? minCardCount : 1))
                : formatCurrency(yearlyPrice.unit_amount * (type === 'TEAM' ? minCardCount : 1))}
            </div>
          </div>
          <div className={styles['payment-invoice']} data-primary={true}>
            <div className={styles['payment-due-date']}>
              <span>Due today </span>
              <span data-positive>({product?.trial_period_days ?? 30} days free)</span>
            </div>
            <div className={styles['payment-amount']}>{`${currency}0.00`}</div>
          </div>
        </div>
        <div className={styles['complete-purchase-btn-container']}>
          <Button width="full" onClick={onClick} loading={loading}>
            Complete purchase
          </Button>
        </div>
      </div>
    </div>
  );
};

export { PaymentSection };
